import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import Article from '../../../smoothr-web-app-core/models/Article';
import { PreorderType } from '../../../smoothr-web-app-core/enums/PreorderType';
import { OrderType } from '../../../smoothr-web-app-core/enums/OrderType';
import ArticleGroup from '../../../smoothr-web-app-core/models/ArticleGroup';
import { OrderUtils } from '../../../smoothr-web-app-core/utils/order-utils';
import { environment } from 'src/environments/environment';
import {
	calculateMinimumPrice,
	defaultsToArticleOption,
	getBasePrice,
	getPrice,
	isFromPriceTag,
	minOfItem,
	numberD,
	numberToCurrency,
} from '../../../smoothr-web-app-core/utils/utils';
import { TOGGLE_TAG } from '../menu-filters/menu-filters.component';
import { TranslateService } from '@ngx-translate/core';
import { ValidationUtils } from 'src/smoothr-web-app-core/utils/validation-utils';
import OptionGroup from 'src/smoothr-web-app-core/models/OptionGroup';
import { AllergensInfoModalComponent } from '../allergens-info-modal/allergens-info-modal.component';
import { ModalController } from '@ionic/angular';
import Order from 'src/smoothr-web-app-core/models/Order';
import { RepositoryService } from '../../../smoothr-web-app-core/services/repository/repository.service';
import { consumerDisplayDiscounts } from '../../shared';

@Component({
	selector: 'app-item-view',
	templateUrl: './item-view.component.html',
	styleUrls: ['item-view.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false,
})
export class ItemViewComponent implements OnInit {
	public order: Order;
	@Input() currency: string;
	@Input() isLoyalty: boolean = false;
	@Input() amountOfPoints: number = 0;
	@Input() categoryName: string = '';
	price: number;
	ot = OrderType;
	environment = environment;
	@Input() hasOrder = false;
	@Output() addLoyaltyArticleClick = new EventEmitter<Article>();
	@Input() canBeAdded: boolean = true;
	@Output() addToBasketEvent = new EventEmitter<Article>();
	@Output() addToBasketCustomizeEvent = new EventEmitter<Article>();
	@Output() removeArticleFromBasket = new EventEmitter<Article>();
	landscape = false;
	priceText = '';
	numberToCurrency = numberToCurrency;
	fromPrice = false;
	measurmentText: string = '';
	basePrice = '';
	checkQuantityIfHasInBasket = 0;
	findFeatureArray: string[] = [];
	checkVeganFeatureValue: string = '';
	showMostWanted = false;
	valueOfSlider = 0;
	filtersArray: string[] = [];
	ifArticleHaveGroups: boolean = false;
	isCustomProduct: boolean = false;

	constructor(
		private translate: TranslateService,
		private modalCtrl: ModalController,
		public repositoryService: RepositoryService
	) {}

	@Input() set selectedToggleTag(value: TOGGLE_TAG) {
		if (this._article) this.checkVeganFeature(this._article, value);
	}

	@Input() set orderValue(value: Order) {
		this.order = value;
		this.currency = value?.currency || this.currency;
		this.hasOrder = !!value;
		this.findAmountOfItems();
		this.reloadPrice();
	}

	private _article: Article;

	get article(): Article {
		return this._article;
	}

	@Input()
	set article(value: Article) {
		this._article = value;
		this.reloadPrice();
		this.checkMeasurmentPlusPfand(value);
		this.showMeasurement(value);
		// this.checkIfLandscape(value.assets.large)
	}

	private _preorderType: PreorderType = null;

	get preorderType(): PreorderType {
		return this._preorderType;
	}

	@Input()
	set preorderType(value: PreorderType) {
		this._preorderType = value;
		this.reloadPrice();
		this.showMeasurement(this._article);
	}

	ngOnInit(): void {
		setTimeout(() => {
			this.findAmountOfItems();
		}, 1000);
	}

	findAmountOfItems() {
		const order = this.order;
		const article = this.article;
		if (!order || !article) {
			this.checkQuantityIfHasInBasket = 0;
			return;
		}
		if (order.orderedArticles.length <= 0) {
			this.checkQuantityIfHasInBasket = 0;
			return;
		}
		const foundItems = order.orderedArticles.filter(it => it.article._id === article._id);
		if (!foundItems || foundItems.length <= 0) {
			this.checkQuantityIfHasInBasket = 0;
			return;
		}
		this.checkQuantityIfHasInBasket = foundItems.reduce((a, b) => a + b.quantity, 0);
	}

	getDisplayPriceText(): string {
		if (isFromPriceTag(this.article)) {
			const fromPriceText = this.translate.instant('common.from');
			return `${fromPriceText} ${this.priceText}`;
		}
		return this.priceText;
	}

	checkVeganFeature(article: Article, toggleTag: TOGGLE_TAG = TOGGLE_TAG.All) {
		const result = Object.entries(article.compound?.features)
			.filter(it => it[0] == 'vegetarian' || it[0] == 'vegan')
			.filter(it => !!it[1])
			.map(it => it[0]);
		if (result && result?.length > 1) {
			this.checkVeganFeatureValue = result[1];
		} else {
			this.checkVeganFeatureValue = result[0];
		}
	}

	findFeature(article: Article) {
		if (this.findFeatureArray.length > 0) {
			return;
		}
		if (article) {
			if (article?.compound?.allergens && article.compound.allergens['cerealsContainingGluten'] == true) {
				this.findFeatureArray.push('gluten');
			}
			if (article?.compound?.allergens && article.compound.allergens['peanutsAndProductsThereof'] == true) {
				this.findFeatureArray.push('peanut');
			}
			if (article?.compound?.allergens && article.compound.allergens['milkAndProductsThereof'] == true) {
				this.findFeatureArray.push('lactose');
			}
			if (article?.compound?.features?.spicyLevel) {
				this.findFeatureArray.push('spicy');
			}
		}
	}

	dependenciesRegardingOptionArticle(article: Article, groups: OptionGroup[], optionArticle: Article): OptionGroup[] {
		return article.groupDependencies
			.map(gd => {
				if (gd.dependencies.find(dependeci => dependeci.groupArticles.includes(optionArticle._id)) == null) {
					return null;
				} else {
					return groups.find(it => gd.group === it._id);
				}
			})
			.filter(it => !!it);
	}

	checkIfLandscape(src: string) {
		const image = new Image();
		image.src = src;
		var height = image.height;
		var width = image.width;
		console.log('checkIfLandscape', height, width);

		if (width > height) {
			console.log(width, height);
			this.landscape = true;
		}
	}

	hasTag(article: Article) {
		if (!article?.compound?.features) {
			return [];
		}
		const tagArray = [];
		if (article.compound?.features?.vegetarian) {
			tagArray.push('vegetarian');
		}
		if (article.compound?.features?.vegan) {
			tagArray.push('vegan');
		}
		//TODO: test
		if (article?.compound?.additives?.containsAlcohol) {
			tagArray.push('alcohol');
		}

		return tagArray;
	}

	checkIfOwnPizza(article: Article) {
		return article?.number == '40144';
	}

	showMeasurement(article: Article) {
		if (article && this._preorderType) {
			if (article?.measurement?.refAmount && article?.measurement?.refUnit && article.measurement.amount && this._preorderType) {
				const orderType = this.order?.type || OrderType.STANDARD;
				const preorderType = this.order?.preorder?.type || this._preorderType;
				if (article?.measurement?.refAmount == 100 && article?.measurement?.refUnit === 'g') {
					this.basePrice =
						'1' + ' ' + 'kg' + ' = ' + numberToCurrency(getBasePrice(article, orderType, preorderType) * 10, this.currency);
					return;
				}
				if (article?.measurement?.refAmount == 100 && article?.measurement?.refUnit === 'ml') {
					this.basePrice =
						'1' + ' ' + 'l' + ' = ' + numberToCurrency(getBasePrice(article, orderType, preorderType) * 10, this.currency);
					return;
				}
				this.basePrice =
					article.measurement.refAmount +
					' ' +
					article.measurement.refUnit +
					' = ' +
					numberToCurrency(getBasePrice(article, orderType, preorderType), this.currency);
				return;
			}
		} else {
			return (this.basePrice = '');
		}
	}

	getArticlePrice(article: Article) {
		const orderType = this.order?.type || OrderType.STANDARD;
		const preorderType = this.order?.preorder?.type || this.preorderType;
		let totalPrice = getPrice(article, orderType, preorderType);
		const tagPrice = this.findTagPrice(this.article);
		if (tagPrice && tagPrice > 0) {
			totalPrice = totalPrice - tagPrice;
		}
		return totalPrice;
	}

	checkPfandTag(article: Article) {
		if (article.tags?.length === 0) {
			return '';
		}
		const deposit25 = article.tags.find(it => it.identifier === 'deposit25') ? 'deposit25' : '';
		const deposit15 = article.tags.find(it => it.identifier === 'deposit15') ? 'deposit15' : '';
		const deposit8 = article.tags.find(it => it.identifier === 'deposit08') ? 'deposit08' : '';
		const deposit28 = article.tags.find(it => it.identifier === 'deposit28') ? 'deposit28' : '';

		return deposit15 || deposit25 || deposit8 || deposit28;
	}

	checkMeasurmentPlusPfand(article: Article) {
		if (!article) {
			return;
		}
		let result = '';

		const pfandValue = this.findTagPrice(article);
		if (pfandValue) {
			result =
				this.translate.instant('pfand_text.zzgl') +
				numberToCurrency(pfandValue, this.currency) +
				this.translate.instant('pfand_text.pfand') +
				'<br/>';
		} else {
			if (result.length > 0) {
				result = result + ')';
			}
		}

		this.measurmentText = result;
	}

	getPricePfandValue(it: string) {
		if (it === 'deposit25') {
			return 0.25;
		}
		if (it === 'deposit15') {
			return 0.15;
		}
		if (it === 'deposit08') {
			return 0.08;
		}
		if (it === 'deposit28') {
			return 0.28;
		}
		return 0;
	}

	findTagPrice(article: Article) {
		if (article && article.tags && article.tags.length > 0 && article.tags.find(it => it.identifier.includes('deposit'))) {
			const foundTag = article?.tags?.find(
				it =>
					it?.identifier === 'deposit25' ||
					it?.identifier === 'deposit15' ||
					it?.identifier === 'deposit08' ||
					it?.identifier === 'deposit28'
			);
			if (foundTag) {
				if (foundTag.identifier === 'deposit25') {
					return 0.25;
				}
				if (foundTag.identifier === 'deposit15') {
					return 0.15;
				}
				if (foundTag.identifier === 'deposit08') {
					return 0.08;
				}
				if (foundTag.identifier === 'deposit28') {
					return 0.28;
				}
				return 0;
			}
			return null;
		} else if (article?.deposit) {
			return numberD(article.deposit);
		} else {
			return null;
		}
	}

	showNutrition(article: Article) {
		AllergensInfoModalComponent.show(this.modalCtrl, article);
	}

	public addLoyaltyItem(article: Article) {
		this.addLoyaltyArticleClick.emit(article);
	}

	public addToBasketCustomize(article: Article) {
		if (this.articleNeedsCustomization(article)) {
			this.addToBasketCustomizeEvent.emit(article);
		} else {
			this.addToBasketEvent.emit(article);
		}
	}

	articleNeedsCustomization(article: Article): boolean {
		if (!article) {
			return false;
		}

		// Articles with special tags always need customization
		if (article.tags && article.tags.length > 0) {
			const specialTags = ['custom_bowl', 'create_own', 'half_pizza'];
			if (article.tags.some(tag => specialTags.includes(tag.identifier))) {
				return true;
			}
		}

		if (!article.groups || article.groups.length === 0) {
			return false;
		}

		const hasCustomizableGroups = article.groups.some(group => {
			if (!group.visible) {
				return false;
			}

			const availableArticles = (group.articles || []).filter(a => a.visible && a.isActive);

			if (availableArticles.length === 0) {
				return false;
			}

			const hasRequiredSelection = group.requiredAmount > 0;
			const canSelectItems = group.limit > 0;
			const isSingleAutomatic = group.limit === 1 && group.requiredAmount === 1 && availableArticles.length === 1;

			return (hasRequiredSelection || canSelectItems) && !isSingleAutomatic;
		});

		return hasCustomizableGroups;
	}

	public addToBasket(article: Article) {
		this.addToBasketEvent.emit(article);
	}

	removeArticle(article: Article) {
		this.removeArticleFromBasket.emit(article);
	}

	public increaseQuantity(article: Article) {
		console.log('increaseQuantity', article);
		if (!article) return;

		if (this.articleNeedsCustomization(article)) {
			this.addToBasketCustomizeEvent.emit(article);
		} else {
			this.addToBasketEvent.emit(article);
		}

		setTimeout(() => {
			this.findAmountOfItems();
		}, 100);
	}

	public decreaseQuantity(article: Article) {
		console.log('decreaseQuantity', article);
		if (!article) return;

		if (this.checkQuantityIfHasInBasket > 0) {
			this.removeArticleFromBasket.emit(article);

			setTimeout(() => {
				this.findAmountOfItems();
			}, 100);
		}
	}

	private reloadPrice() {
		if (this.article && this.article.piggyRewardUuid && this.article?.piggyReward?.required_credits && this.isLoyalty) {
			this.valueOfSlider = this.amountOfPoints / this.article?.piggyReward?.required_credits;
			console.log('VALUE OF SLDIER', this.valueOfSlider);
		}
		if (this.article?.groups?.length > 0 && this.article.groups.filter(it => it.visible)?.length > 0) {
			this.ifArticleHaveGroups = true;
		}
		if (this.article.tags.length > 0 && this.article.tags.find(it => it.identifier == 'create_own')) {
			this.isCustomProduct = true;
		}
		this.fromPrice = false;
		const articleGroup = new ArticleGroup();
		articleGroup.article = this.article;

		// Use consistent order types: default to PREORDER if no order exists, otherwise use order's type
		const orderType = this.order?.type || OrderType.STANDARD;
		const preorderType = this.order?.preorder?.type || this.preorderType;

		articleGroup.groups.push(...defaultsToArticleOption(this.article, [], this.article.defaults, orderType, preorderType));

		articleGroup.quantity = 1;

		const article = structuredClone(articleGroup.article);
		let price = OrderUtils.totalPrice(articleGroup, orderType, preorderType);
		const processedGroups: string[] = [];

		if (article?.compound?.features?.new) {
			this.showMostWanted = true;
		}
		article.groups.forEach(grp => {
			if (grp.requiredAmount > 0 && grp.visible && grp.articles.length > 0) {
				this.fromPrice = true;
			}
			if (processedGroups.indexOf(grp._id) >= 0) return;
			if (ValidationUtils.isGroupDependencyFulfilled(articleGroup.article, articleGroup.groups, grp).times < 0) return;
			if (grp.requiredAmount == grp.limit) {
				const minimumItem = minOfItem(grp.articles, opt => getPrice(opt, orderType, preorderType, null));
				price += getPrice(minimumItem, orderType, preorderType, null) * grp.requiredAmount;

				processedGroups.push(grp._id);
			}
		});

		// If the article has the fromPrice tag, calculate the minimum possible price
		if (this.fromPrice || isFromPriceTag(this.article)) {
			price = calculateMinimumPrice(this.article, orderType, preorderType, 0);
		}

		const tagPrice = this.findTagPrice(this.article);
		this.price = price - tagPrice;
		this.priceText = numberToCurrency(this.price, this.currency);

		this.checkVeganFeature(this.article);
		this.findFeature(this.article);

		return;
	}

	protected readonly consumerDisplayDiscounts = consumerDisplayDiscounts;
}
